import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArchiveExplorer } from './archiveExplorer';
import { Filters } from './filters';
import { TreeControlDefaultState, TreeControlState } from 'components/explorer';
import { Preview } from './preview';
import { useRMDispatch } from 'redux/hooks';
import { fetchActiveArchivingJobResultsThunk } from 'redux/thunks/archiving';
import {
  useFetchActiveArchivingJobResults,
  useArchivingJobErrorNotification,
  useIsArchivingJobActive,
} from './hooks';
import { ARCHIVED_FILES_SECTION_PARAM, ARCHIVED_FILES_SECTION_VALUE } from './constants';

const RESULTS_TAB_INDEX = 1;

export const Archive = () => {
  useArchivingJobErrorNotification();
  const isJobActive = useIsArchivingJobActive();
  const dispatch = useRMDispatch();

  // Deep-link support: the notification card's "See Result" opens
  // /setup/archiving?tab=results&jobId=<id> to show a specific (completed) job's results.
  // The unarchive side uses the same path plus &section=archivedFiles — ignore the jobId/tab
  // in that case so this (archive-side) effect doesn't fetch an unarchive job's results.
  const { search, key: locationKey } = useLocation();
  const searchParams = new URLSearchParams(search);
  const isUnarchiveSection =
    searchParams.get(ARCHIVED_FILES_SECTION_PARAM) === ARCHIVED_FILES_SECTION_VALUE;
  const requestedTab = isUnarchiveSection ? null : searchParams.get('tab');
  const requestedJobId = isUnarchiveSection ? null : searchParams.get('jobId');
  useFetchActiveArchivingJobResults(requestedJobId);

  const [treeControlState, setTreeControlState] = useState<TreeControlState>({
    ...TreeControlDefaultState,
  });
  const [selectedPreviewTab, setSelectedPreviewTab] = useState(() =>
    requestedTab === 'results' || isJobActive ? RESULTS_TAB_INDEX : 0,
  );
  const isResultsTab = selectedPreviewTab === RESULTS_TAB_INDEX;

  useEffect(() => {
    if (isJobActive) {
      setSelectedPreviewTab(RESULTS_TAB_INDEX);
    }
  }, [isJobActive]);

  // locationKey changes on every navigation, even a repeat click of "See Result" for the
  // same job that leaves the URL unchanged, so the effect re-fires and moves back to the
  // Results tab instead of being skipped because requestedJobId didn't change value.
  useEffect(() => {
    if (requestedJobId) {
      dispatch(fetchActiveArchivingJobResultsThunk(requestedJobId));
      setSelectedPreviewTab(RESULTS_TAB_INDEX);
    }
  }, [requestedJobId, locationKey, dispatch]);

  return (
    <div className="tab-content-height w-full flex flex-row">
      <div className="basis-[25%] w-[25%] border-solid border-0 border-r-2 border-[#E0E0E0] flex flex-col p-2 gap-2">
        <div className="flex flex-col basis-[63%] w-full">
          <ArchiveExplorer
            treeControlState={treeControlState}
            setTreeControlState={setTreeControlState}
            disabled={isResultsTab}
          />
        </div>
        <Filters
          selectedNodes={treeControlState.checkedExplorerNodes}
          onPreviewTabSelect={() => setSelectedPreviewTab(0)}
          disabled={isResultsTab}
        />
      </div>
      <div className="basis-[75%] w-[75%] flex h-full">
        <Preview
          selectedNodes={treeControlState.checkedExplorerNodes}
          selectedTab={selectedPreviewTab}
          onSelectedTabChange={setSelectedPreviewTab}
          resultsJobId={requestedJobId}
        />
      </div>
    </div>
  );
};
